import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Statistic,
  Select,
  Modal,
  message,
} from "antd";
import {
  PlusOutlined,
  SyncOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useInspections } from "../../../hooks/academics/useInspections";
import InspectionTable from "../../../components/academics/inspections/InspectionTable";
import InspectionDetailDrawer from "../../../components/academics/inspections/InspectionDetailDrawer";
import CreateInspectionModal from "../../../components/academics/inspections/CreateInspectionModal";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { Option } = Select;

const InspectionManagement = () => {
  const navigate = useNavigate();
  const {
    inspections,
    loading,
    choices,
    fetchChoices,
    fetchInspections,
    createInspection,
    updateInspection,
    deleteInspection,
    downloadPdf,
    regeneratePdf,
    getStatistics,
  } = useInspections();

  // Bootstrap data
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [terms, setTerms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);

  // UI state
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [editingInspection, setEditingInspection] = useState(null);
  const [creating, setCreating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [regeneratingPdf, setRegeneratingPdf] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    classId: null,
    streamId: null,
    termId: null,
    acadyrId: null,
  });

  // Fetch bootstrap data
  const fetchBootstrapData = useCallback(async () => {
    try {
      // Fetch classes
      const classesRes = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.classes,
        null,
        navigate,
      );
      if (classesRes.ok) {
        setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
      }

      // Fetch streams
      const streamsRes = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.stream,
        null,
        navigate,
      );
      if (streamsRes.ok) {
        setStreams(Array.isArray(streamsRes.data) ? streamsRes.data : []);
      }

      // Fetch terms
      const termsRes = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.enrollment.terms,
        null,
        navigate,
      );
      if (termsRes.ok) {
        setTerms(Array.isArray(termsRes.data) ? termsRes.data : []);
      }

      // Fetch academic years
      const acadYearsRes = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.enrollment.academicYears,
        null,
        navigate,
      );
      if (acadYearsRes.ok) {
        setAcademicYears(
          Array.isArray(acadYearsRes.data) ? acadYearsRes.data : [],
        );
      }

      // Fetch subjects
      const subjectsRes = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.subject,
        null,
        navigate,
      );
      if (subjectsRes.ok) {
        setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : []);
      }
    } catch (error) {
      console.error("Error fetching bootstrap data:", error);
    }
  }, [navigate]);

  // Fetch students for a specific class with additional filters
  const fetchStudentsForClass = useCallback(
    async (classId, streamId = null, acadyrId = null, termId = null) => {
      if (!classId) {
        setStudents([]);
        return;
      }
      try {
        // Find the class, stream, and academic year names for filtering
        const selectedClass = classes.find((c) => c.id === classId);
        const selectedStream = streamId
          ? streams.find((s) => s.id === streamId)
          : null;
        const selectedAcadyr = acadyrId
          ? academicYears.find((a) => a.id === acadyrId)
          : null;

        const queryParams = new URLSearchParams();
        queryParams.append("status", "ADMITTED"); // Only fetch admitted students
        if (selectedClass) {
          queryParams.append("className", selectedClass.name);
        }
        if (selectedStream) {
          queryParams.append("streamName", selectedStream.name);
        }
        if (selectedAcadyr) {
          queryParams.append("acadyr", selectedAcadyr.acadyr);
        }
        // Note: term filtering is not supported by the backend filter

        const url = `${domainSettings.urls.enrollment.students}?${queryParams.toString()}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          setStudents(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error("Error fetching students:", error);
      }
    },
    [navigate, classes, streams, academicYears],
  );

  useEffect(() => {
    fetchBootstrapData();
    fetchChoices();
    fetchInspections();
  }, []);

  useEffect(() => {
    fetchInspections(filters);
  }, [filters]);

  const handleViewDetails = (inspection) => {
    setSelectedInspection(inspection);
    setDetailsDrawerVisible(true);
  };

  const handleEdit = (inspection) => {
    setEditingInspection(inspection);
    fetchStudentsForClass(
      inspection.classID,
      inspection.streamID,
      inspection.acadyrID,
      inspection.termID,
    );
    setCreateModalVisible(true);
  };

  const handleDownloadPdf = async (inspection) => {
    setDownloadingPdf(inspection.id);
    const filename =
      `inspection_${inspection.className}_${inspection.termName}_${inspection.academicYear}.pdf`.replace(
        /\s+/g,
        "_",
      );
    await downloadPdf(inspection.id, filename);
    setDownloadingPdf(null);
  };

  const handleRegeneratePdf = async (inspection) => {
    Modal.confirm({
      title: "Regenerate PDF",
      content: `Regenerate PDF for ${inspection.className} - ${inspection.termName}? This will replace the existing PDF.`,
      okText: "Yes, Regenerate",
      okType: "primary",
      onOk: async () => {
        setRegeneratingPdf(inspection.id);
        await regeneratePdf(inspection.id);
        setRegeneratingPdf(null);
      },
    });
  };

  const handleDelete = async (inspection) => {
    await deleteInspection(inspection.id);
  };

  const handleCreateInspection = async (data) => {
    setCreating(true);
    try {
      let result;
      if (editingInspection) {
        // Update existing inspection
        result = await updateInspection(editingInspection.id, data);
      } else {
        // Create new inspection
        result = await createInspection(data);
      }

      if (result) {
        setCreateModalVisible(false);
        setEditingInspection(null);
        return true;
      }
      return false;
    } finally {
      setCreating(false);
    }
  };

  const handleCancelModal = () => {
    setCreateModalVisible(false);
    setEditingInspection(null);
  };

  const handleClassChangeInModal = (classId, formData) => {
    const streamId = formData?.streamID;
    const acadyrId = formData?.acadyrID;
    const termId = formData?.termID;
    fetchStudentsForClass(classId, streamId, acadyrId, termId);
  };

  const handleFiltersChangeInModal = (formData) => {
    fetchStudentsForClass(
      formData.classID,
      formData.streamID,
      formData.acadyrID,
      formData.termID,
    );
  };

  const stats = getStatistics();

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Statistics Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Inspections"
                value={stats.total}
                valueStyle={{ color: "#1890ff" }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Avg. General Score"
                value={stats.averageGeneralScore}
                suffix="/100"
                valueStyle={{
                  color:
                    stats.averageGeneralScore >= 70 ? "#3f8600" : "#faad14",
                }}
                prefix={<BarChartOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Avg. Skill Score"
                value={stats.averageSkillScore}
                suffix="/300"
                valueStyle={{
                  color: stats.averageSkillScore >= 210 ? "#3f8600" : "#faad14",
                }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Classes Inspected"
                value={new Set(inspections.map((i) => i.classID)).size}
                valueStyle={{ color: "#722ed1" }}
                prefix={<BookOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Content */}
        <Card
          title={
            <Space>
              <FileTextOutlined />
              <span>Academic Inspections</span>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {/* Action Buttons */}
            <Row gutter={[8, 8]}>
              <Col xs={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                  >
                    New Inspection
                  </Button>
                  <Button
                    icon={<SyncOutlined />}
                    onClick={() => fetchInspections(filters)}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Filters */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Filter by Class"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.classId}
                  onChange={(value) =>
                    setFilters({ ...filters, classId: value })
                  }
                  showSearch
                  optionFilterProp="children"
                >
                  {classes.map((cls) => (
                    <Option key={cls.id} value={cls.id}>
                      {cls.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Filter by Term"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.termId}
                  onChange={(value) =>
                    setFilters({ ...filters, termId: value })
                  }
                >
                  {terms.map((term) => (
                    <Option key={term.id} value={term.id}>
                      {term.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Filter by Academic Year"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.acadyrId}
                  onChange={(value) =>
                    setFilters({ ...filters, acadyrId: value })
                  }
                >
                  {academicYears.map((year) => (
                    <Option key={year.id} value={year.id}>
                      {year.acadyr}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>

            {/* Inspection Table */}
            <InspectionTable
              inspections={inspections}
              loading={loading}
              onViewDetails={handleViewDetails}
              onEdit={handleEdit}
              onDownloadPdf={handleDownloadPdf}
              onRegeneratePdf={handleRegeneratePdf}
              onDelete={handleDelete}
              downloadingPdf={downloadingPdf}
              regeneratingPdf={regeneratingPdf}
            />
          </Space>
        </Card>
      </Space>

      {/* Detail Drawer */}
      <InspectionDetailDrawer
        visible={detailsDrawerVisible}
        onClose={() => {
          setDetailsDrawerVisible(false);
          setSelectedInspection(null);
        }}
        inspectionId={selectedInspection?.id}
        onDownloadPdf={handleDownloadPdf}
        onRegeneratePdf={handleRegeneratePdf}
      />

      {/* Create/Edit Modal */}
      <CreateInspectionModal
        visible={createModalVisible}
        onCancel={handleCancelModal}
        onSubmit={handleCreateInspection}
        loading={creating}
        classes={classes}
        streams={streams}
        terms={terms}
        academicYears={academicYears}
        subjects={subjects}
        students={students}
        choices={choices}
        onClassChange={handleClassChangeInModal}
        onFiltersChange={handleFiltersChangeInModal}
        inspection={editingInspection}
      />
    </>
  );
};

export default InspectionManagement;
